import React, { useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import TradeAttachmentGallery from "@/components/TradeAttachmentGallery";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API_URL } from "@/lib/api";

const ACCEPTED_IMAGE_TYPES = "image/png,image/jpeg,image/jpg,image/gif,image/webp";

function getUploadErrorMessage(error) {
  return error?.response?.data?.detail || "Error uploading screenshot";
}

export default function TradeAttachmentsField({
  attachments = [],
  onChange,
  inputId,
  label = "Screenshots",
  disabled = false,
  onUploadingChange,
}) {
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) {
      return;
    }

    const hasInvalidFile = files.some((file) => !file.type.startsWith("image/"));
    if (hasInvalidFile) {
      toast.error("Only image files can be attached");
      event.target.value = "";
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Your session expired. Please log in again.");
      event.target.value = "";
      return;
    }

    setUploading(true);
    onUploadingChange?.(true);

    try {
      const uploadedAttachments = await Promise.all(
        files.map(async (file) => {
          const formData = new FormData();
          formData.append("file", file);

          const response = await axios.post(`${API_URL}/upload`, formData, {
            headers: { Authorization: `Bearer ${token}` },
          });

          return response.data.url;
        })
      );

      onChange([...attachments, ...uploadedAttachments]);

      toast.success(
        `${uploadedAttachments.length} screenshot${uploadedAttachments.length > 1 ? "s" : ""} attached`
      );
    } catch (error) {
      toast.error(getUploadErrorMessage(error));
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
      event.target.value = "";
    }
  };

  const handleRemove = (indexToRemove) => {
    onChange(attachments.filter((_, index) => index !== indexToRemove));
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label htmlFor={inputId} className="text-sm font-semibold">
          {label}
        </Label>
        <Input
          id={inputId}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES}
          multiple
          onChange={handleFileChange}
          disabled={disabled || uploading}
          className="cursor-pointer border-primary/20 focus:border-primary transition-all file:cursor-pointer"
        />
        <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
          <p>Attach one or more screenshots. PNG, JPG, GIF, WebP up to 5 MB each.</p>
          {uploading ? (
            <span className="inline-flex items-center gap-2 whitespace-nowrap font-medium text-primary">
              <Loader2 className="h-4 w-4 animate-spin" />
              Uploading...
            </span>
          ) : null}
        </div>
      </div>

      <TradeAttachmentGallery
        attachments={attachments}
        onRemove={handleRemove}
        removeDisabled={disabled || uploading}
      />
    </div>
  );
}
